import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";

const PasswordField = ({ label, testid, value, error, onChange, autoComplete }) => (
  <label className="block"><span className="field-label">{label}</span>
    <input type="password" data-testid={testid} value={value} autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)} className={`inp ${error ? "inp-invalid" : ""}`} />
    {error && <span data-testid={`${testid.replace("-input", "")}-error`} className="field-error mt-1 block">{error}</span>}
  </label>
);

const clientErrors = (current, next) => {
  if (!current) return { current: "Current password is required" };
  if (!next) return { next: "New password is required" };
  if (next.length < 8) return { next: "Password must be at least 8 characters" };
  return {};
};

const ChangePasswordForm = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const edit = (setter) => (v) => { setter(v); setErrors({}); setDone(false); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = clientErrors(current, next);
    if (Object.keys(errs).length) return setErrors(errs);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setDone(true);
      toast.success("Password updated");
    } catch (err) {
      const msg = errMsg(err);
      setErrors(msg.startsWith("Current") ? { current: msg } : { next: msg });
    }
  };

  return (
    <form onSubmit={submit} data-testid="account-password-form" className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <h2 className="section-title flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" /> Change password</h2>
      <PasswordField label="Current password" testid="account-current-password-input" value={current} error={errors.current}
        onChange={edit(setCurrent)} autoComplete="current-password" />
      <PasswordField label="New password (min 8 characters)" testid="account-new-password-input" value={next} error={errors.next}
        onChange={edit(setNext)} autoComplete="new-password" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {done ? <p data-testid="account-password-success" className="text-sm font-medium text-emerald-400">Password updated</p> : <span />}
        <button type="submit" data-testid="account-change-password-btn" className="btn-primary">Update password</button>
      </div>
    </form>
  );
};

export default function AccountPage() {
  const { user } = useAuth();
  const { repsById } = useData();
  const rep = user.rep_id ? repsById[user.rep_id] : null;
  return (
    <div className="mx-auto max-w-xl space-y-5" data-testid="account-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Account</h1>
        <p className="text-sm text-slate-400">Your sign-in details.</p>
      </div>
      <dl className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-4 text-sm" data-testid="account-details">
        <div><dt className="field-label">Name</dt><dd data-testid="account-name" className="break-words">{user.name}</dd></div>
        <div><dt className="field-label">Username</dt><dd data-testid="account-username" className="break-words font-mono">{user.username}</dd></div>
        <div><dt className="field-label">Role</dt><dd data-testid="account-role">{user.role}</dd></div>
        <div><dt className="field-label">Linked rep</dt><dd data-testid="account-rep">{rep ? `${rep.name} (${rep.role})` : "None"}</dd></div>
      </dl>
      <ChangePasswordForm />
    </div>
  );
}
